import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Download, Loader2, LogOut } from "lucide-react";
import { DigitalPulpitMark } from "@/components/landing/Logo";
import { adminLogin, getLeads, exportLeadsUrl } from "@/lib/api";

const TYPES = ["all", "audit", "checklist", "webinar"];

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("mosm_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    getLeads(token)
      .then((r) => setLeads(r.data))
      .catch(() => {
        sessionStorage.removeItem("mosm_admin_token");
        setToken("");
      });
  }, [token]);

  const onLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await adminLogin(email, password);
      sessionStorage.setItem("mosm_admin_token", data.token);
      setToken(data.token);
    } catch {
      setError("Invalid credentials. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem("mosm_admin_token");
    setToken("");
    setLeads([]);
  };

  const onExport = async () => {
    const res = await axios.get(exportLeadsUrl, {
      headers: { Authorization: `Bearer ${token}` },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mosm-leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-4">
        <form onSubmit={onLogin} data-testid="admin-login-form"
          className="w-full max-w-sm rounded-xl border border-gold/30 bg-navy-surface p-8 shadow-[0_0_50px_rgba(212,175,55,0.08)]">
          <div className="flex flex-col items-center mb-8">
            <DigitalPulpitMark size={40} />
            <p className="mt-4 font-display text-lg uppercase tracking-tight text-white">Leads Dashboard</p>
            <p className="text-[10px] tracking-[0.3em] uppercase text-slate-500 mt-1">Admin access only</p>
          </div>
          <input data-testid="admin-email-input" className="field mb-4" type="email" placeholder="Admin email"
            required value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-password-input" className="field mb-4" type="password" placeholder="Password"
            required value={password} onChange={(e) => setPassword(e.target.value)} />
          {error && <p data-testid="admin-login-error" className="mb-4 text-xs text-red-400">{error}</p>}
          <button data-testid="admin-login-submit-button" disabled={busy}
            className="w-full rounded-full bg-gold px-6 py-3.5 text-[11px] font-bold tracking-[0.18em] uppercase text-navy transition-all hover:bg-gold-hover disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 size={15} className="animate-spin" />}
            Sign In
          </button>
          <Link to="/" data-testid="admin-back-home-link"
            className="block mt-5 text-center text-[11px] tracking-[0.2em] uppercase text-slate-500 hover:text-gold transition-colors">
            ← Back to site
          </Link>
        </form>
      </div>
    );
  }

  const shown = leads.filter((l) => filter === "all" || l.type === filter);
  const countOf = (t) => (t === "all" ? leads.length : leads.filter((l) => l.type === t).length);

  return (
    <div className="min-h-screen bg-navy text-white" data-testid="admin-dashboard">
      <header className="border-b border-gold/20 bg-navy-surface/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <DigitalPulpitMark size={30} />
            <span className="font-display text-base uppercase tracking-tight">Leads Dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="leads-export-button" onClick={onExport}
              className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[10px] font-bold tracking-[0.18em] uppercase text-navy transition-all hover:bg-gold-hover">
              <Download size={14} /> Export CSV
            </button>
            <button data-testid="admin-logout-button" onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 text-[10px] font-bold tracking-[0.18em] uppercase text-slate-300 transition-all hover:border-gold hover:text-gold">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap gap-3 mb-8" data-testid="leads-filters">
          {TYPES.map((t) => (
            <button key={t} data-testid={`lead-filter-${t}`} onClick={() => setFilter(t)}
              className={`rounded-full border px-5 py-2 text-[11px] font-bold tracking-[0.15em] uppercase transition-all ${filter === t
                ? "border-gold bg-gold text-navy" : "border-white/20 text-slate-300 hover:border-gold/60 hover:text-gold"}`}>
              {t} · <span data-testid={`lead-count-${t}`}>{countOf(t)}</span>
            </button>
          ))}
        </div>

        <div className="rounded-xl border border-white/10 bg-navy-surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="border-b border-gold/20 text-left text-[10px] tracking-[0.22em] uppercase text-gold">
                <th className="px-5 py-4">Type</th>
                <th className="px-5 py-4">Date</th>
                <th className="px-5 py-4">Church</th>
                <th className="px-5 py-4">Contact</th>
                <th className="px-5 py-4">Email</th>
                <th className="px-5 py-4">Details</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-5 py-3.5">
                    <span className="rounded-full bg-gold/10 border border-gold/30 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-gold">
                      {l.type}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-400 text-xs whitespace-nowrap">
                    {l.created_at ? new Date(l.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-5 py-3.5 text-slate-200">{l.church_name || "—"}</td>
                  <td className="px-5 py-3.5 text-slate-200">{l.leader_name || l.name || "—"}</td>
                  <td className="px-5 py-3.5 text-slate-300">{l.email}</td>
                  <td className="px-5 py-3.5 text-slate-400 text-xs max-w-[16rem] truncate"
                    title={l.challenge || l.vision || l.role || l.city_state || ""}>
                    {l.challenge || l.vision || l.role || l.city_state || "—"}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500 text-sm"
                  data-testid="leads-empty-state">No leads yet in this category.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
